import { Show } from '@refinedev/antd';
import { useShow, useList, useCan } from '@refinedev/core';
import { Typography, Tag, Descriptions, List as AntList, Card, Tooltip, Space } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { usersWithRolePath } from './utils/usersWithRolePath';
import { ChangeLogLink } from '../../components/changeLog';
import { useRoleAccess } from './utils/roleAccess';

interface Role {
  id: string;
  _id?: string;
  name: string;
  description?: string;
  scope?: 'internal' | 'customer';
  companyId?: string | null;
  companyName?: string | null;
  permissions: string[];
  isSystem?: boolean;
  userCount?: number;
}

import { Permission } from '../permissions/types';
import { groupLabel, groupPermissions } from '../permissions/grouping';

export const RoleDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const access = useRoleAccess();

  const { queryResult, showId } = useShow<Role>({
    resource: 'roles',
    meta: { dataProviderName: 'authDataProvider' },
  });
  // Role history is admin only: the sign-in service answers 404 to customer role managers, since
  // a role's past can name internal permissions. This only hides the link.
  const { data: canSeeHistory } = useCan({ resource: 'roles', action: 'history' });

  const { data: permissionsData } = useList<Permission>({
    resource: 'permissions',
    meta: { dataProviderName: 'authDataProvider' },
    pagination: { mode: 'off' },
  });

  const role = queryResult?.data?.data;
  const allPermissions = permissionsData?.data ?? [];
  const permissionMap = new Map(allPermissions.map((p) => [p.key, p]));

  const renderPermission = (permKey: string) => {
    const perm = permissionMap.get(permKey);
    return (
      <AntList.Item
        // A permission's own page needs permissions:read; without it the key is plain text.
        style={access.canOpenPermission ? { cursor: 'pointer' } : undefined}
        onClick={
          access.canOpenPermission
            ? () => navigate(`/permissions/${encodeURIComponent(permKey)}`)
            : undefined
        }
      >
        <Tooltip title={access.canOpenPermission ? 'Click to view permission details' : undefined}>
          <Tag color="purple" style={access.canOpenPermission ? { cursor: 'pointer' } : undefined}>
            <Typography.Text code>{permKey}</Typography.Text>
          </Tag>
          {perm?.description && (
            <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
              {perm.description}
            </Typography.Text>
          )}
        </Tooltip>
      </AntList.Item>
    );
  };

  const scope = role?.scope ?? 'internal';

  return (
    <Show
      title={
        <span>
          {role?.name || 'Role Detail'}
          {role?.isSystem && (
            <Tag icon={<LockOutlined />} color="default" style={{ marginLeft: 8 }}>
              System Role
            </Tag>
          )}
        </span>
      }
      headerButtons={({ defaultButtons }) => (
        <Space>
          {canSeeHistory?.can && showId !== undefined ? (
            <ChangeLogLink
              key={String(showId)}
              subject={{ kind: 'role', roleId: String(showId) }}
            />
          ) : null}
          {defaultButtons}
        </Space>
      )}
    >
      <Descriptions bordered column={1}>
        <Descriptions.Item label="Name">{role?.name || '—'}</Descriptions.Item>
        <Descriptions.Item label="Description">{role?.description || '—'}</Descriptions.Item>
        <Descriptions.Item label="Scope">
          <Tag color={scope === 'customer' ? 'orange' : 'purple'}>{scope}</Tag>
        </Descriptions.Item>
        {scope === 'customer' && (
          <>
            <Descriptions.Item label="Company ID">{role?.companyId || '—'}</Descriptions.Item>
            <Descriptions.Item label="Company Name">{role?.companyName || '—'}</Descriptions.Item>
          </>
        )}
        <Descriptions.Item label="User Count">
          {role?.name && typeof role.userCount === 'number' && !access.canOpenUsers ? (
            // The people list needs admin:users:read, so show the number without a link.
            role.userCount
          ) : role?.name && typeof role.userCount === 'number' ? (
            <Link to={usersWithRolePath(role.name)} title="See the people with this role">
              {role.userCount}
            </Link>
          ) : (
            '—'
          )}
        </Descriptions.Item>
      </Descriptions>

      <Card title="Permissions" style={{ marginTop: 16 }}>
        {role?.permissions?.length ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {groupPermissions(
              role.permissions.map((key) => ({ key, category: permissionMap.get(key)?.category })),
            ).map(({ category, groups }) => (
              <div key={category}>
                <Typography.Text strong style={{ textTransform: 'capitalize' }}>
                  {category}
                </Typography.Text>
                {groups.map(({ group, items }) => {
                  // An area with only one group needs no group heading.
                  const flat = groups.length === 1;
                  return (
                    <div key={group} style={flat ? undefined : { marginTop: 8, marginLeft: 12 }}>
                      {!flat && (
                        <Typography.Text type="secondary">
                          {groupLabel(group)} ({items.length})
                        </Typography.Text>
                      )}
                      <AntList
                        size="small"
                        dataSource={items.map((p) => p.key)}
                        renderItem={renderPermission}
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </Space>
        ) : (
          <Typography.Text type="secondary">No permissions assigned</Typography.Text>
        )}
      </Card>
    </Show>
  );
};
